import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Home as HomeIcon, Users, Building2, ShieldCheck, MessageSquare, Plus, Trash2, Pencil, Calendar, FileText, Check as CheckIcon, X as XIcon } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="p-20 text-[#86868b]">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const tabs = [
    { id: "overview", label: t.dashboard.overview, icon: LayoutDashboard },
    { id: "properties", label: t.dashboard.properties, icon: HomeIcon },
    { id: "bookings", label: lang === "fr" ? "Réservations" : "Bookings", icon: Calendar },
    { id: "applications", label: lang === "fr" ? "Candidatures" : "Applications", icon: FileText },
    { id: "users", label: t.dashboard.users, icon: Users },
    { id: "agencies", label: t.dashboard.agencies, icon: Building2 },
    { id: "roles", label: t.dashboard.roles, icon: ShieldCheck },
    { id: "messages", label: t.dashboard.messages, icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <div className="flex flex-col lg:flex-row">
        <aside className="lg:w-64 bg-[#f5f5f7] lg:min-h-screen p-6 lg:sticky lg:top-0">
          <div className="font-semibold text-[15px] mb-8 tracking-tight">{t.dashboard.title}</div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {tabs.map((T) => (
              <button
                key={T.id}
                onClick={() => setTab(T.id)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-[13px] whitespace-nowrap transition-colors ${
                  tab === T.id ? "bg-[#1d1d1f] text-white" : "text-[#1d1d1f] hover:bg-black/5"
                }`}
                data-testid={`tab-${T.id}`}
              >
                <T.icon size={15} strokeWidth={1.6} />
                {T.label}
              </button>
            ))}
          </nav>
          <div className="mt-10 text-[12px] text-[#86868b]">
            <div>{user.email}</div>
            <div>{user.role}</div>
          </div>
        </aside>

        <main className="flex-1 p-6 sm:p-10">
          {tab === "overview" && <Overview lang={lang} />}
          {tab === "properties" && <PropertiesTab t={t} lang={lang} />}
          {tab === "bookings" && <BookingsTab lang={lang} />}
          {tab === "applications" && <ApplicationsTab lang={lang} />}
          {tab === "users" && <UsersTab t={t} lang={lang} me={user} />}
          {tab === "agencies" && <AgenciesTab t={t} lang={lang} />}
          {tab === "roles" && <RolesTab lang={lang} />}
          {tab === "messages" && <MessagesTab lang={lang} />}
        </main>
      </div>
    </div>
  );
}

function Overview({ lang }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-[#86868b]">…</div>;
  const items = [
    { label: lang === "fr" ? "Annonces" : "Listings", value: stats.total_properties },
    { label: lang === "fr" ? "Utilisateurs" : "Users", value: stats.total_users },
    { label: lang === "fr" ? "Agences" : "Agencies", value: stats.total_agencies },
    { label: lang === "fr" ? "Messages" : "Messages", value: stats.total_messages },
  ];
  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">
        {lang === "fr" ? "Vue d'ensemble" : "Overview"}
      </h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
        {items.map((it) => (
          <div key={it.label} className="rounded-2xl bg-[#f5f5f7] p-6">
            <div className="text-[11px] uppercase tracking-wider text-[#86868b]">{it.label}</div>
            <div className="text-[36px] font-semibold tracking-tight mt-1 text-[#1d1d1f]">{it.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <div className="rounded-2xl bg-white border border-black/5 p-6">
          <div className="text-[12px] uppercase tracking-wider text-[#86868b] mb-4">
            {lang === "fr" ? "Annonces par catégorie" : "Listings by category"}
          </div>
          {Object.entries(stats.properties_by_category).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
              <span className="text-[14px]">{k}</span>
              <span className="text-[14px] font-medium">{v}</span>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-white border border-black/5 p-6">
          <div className="text-[12px] uppercase tracking-wider text-[#86868b] mb-4">
            {lang === "fr" ? "Agences par plan" : "Agencies by plan"}
          </div>
          {Object.entries(stats.agencies_by_plan).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
              <span className="text-[14px] capitalize">{k}</span>
              <span className="text-[14px] font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PropertiesTab({ t, lang }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "sale", property_type: "apartment",
    price: "", location: "", city: "", bedrooms: 0, bathrooms: 0, surface: 0,
    amenities: "",
  });

  const load = () => api.get("/properties", { params: { limit: 100 } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), surface: Number(form.surface),
        images: images.map((i) => i.url),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/properties", payload);
      toast.success(lang === "fr" ? "Annonce créée" : "Listing created");
      setShowForm(false);
      setImages([]);
      setForm({ title: "", description: "", category: "sale", property_type: "apartment", price: "", location: "", city: "", bedrooms: 0, bathrooms: 0, surface: 0, amenities: "" });
      load();
    } catch {
      toast.error("Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "fr" ? "Supprimer ?" : "Delete?")) return;
    await api.delete(`/properties/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#1d1d1f]">{t.dashboard.properties}</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-pill btn-primary text-[13px] flex items-center gap-2" data-testid="add-property-btn">
          <Plus size={14} /> {t.dashboard.addProperty}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl bg-[#f5f5f7] p-6 mb-8 space-y-4" data-testid="property-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Inp label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="form-title" />
            <Inp label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="form-city" />
            <Inp label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="form-location" />
            <Inp label="Price (EUR)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="form-price" />
            <Sel label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={[["sale","Vente"],["rental_long","Long"],["rental_short","Saisonnière"]]} testid="form-category" />
            <Sel label="Type" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} options={[["apartment","Appartement"],["house","Maison"],["villa","Villa"],["studio","Studio"],["land","Terrain"]]} testid="form-type" />
            <Inp label="Bedrooms" type="number" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} testid="form-beds" />
            <Inp label="Bathrooms" type="number" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="form-baths" />
            <Inp label="Surface (m²)" type="number" value={form.surface} onChange={(v) => setForm({ ...form, surface: v })} testid="form-surface" />
            <Inp label="Amenities (séparées par virgule)" value={form.amenities} onChange={(v) => setForm({ ...form, amenities: v })} testid="form-amenities" />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-2">Photos</label>
            <ImageUpload value={images} onChange={setImages} max={12} />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-white rounded-xl px-4 py-2 text-[14px] outline-none" data-testid="form-description" />
          </div>
          <button type="submit" className="btn-pill btn-primary text-[13px]" data-testid="form-submit">
            {lang === "fr" ? "Créer" : "Create"}
          </button>
        </form>
      )}

      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]" data-testid="properties-table">
          <thead className="bg-[#f5f5f7] text-[#86868b]">
            <tr>
              <Th>Title</Th><Th>Category</Th><Th>City</Th><Th>Price</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.property_id} className="border-t border-black/5">
                <Td>{p.title}</Td>
                <Td className="text-[#86868b]">{p.category}</Td>
                <Td>{p.city}</Td>
                <Td>{p.price.toLocaleString()} €</Td>
                <Td>{p.status}</Td>
                <Td>
                  <button onClick={() => remove(p.property_id)} className="text-[#86868b] hover:text-red-600" data-testid={`delete-${p.property_id}`}>
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab({ t, lang, me }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateRole = async (id, role) => {
    await api.patch(`/admin/users/${id}/role`, { role });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm(lang === "fr" ? "Supprimer ?" : "Delete?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">{t.dashboard.users}</h1>
      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]" data-testid="users-table">
          <thead className="bg-[#f5f5f7] text-[#86868b]">
            <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-black/5">
                <Td>{u.name}</Td>
                <Td className="text-[#86868b]">{u.email}</Td>
                <Td>
                  <select
                    value={u.role}
                    onChange={(e) => updateRole(u.user_id, e.target.value)}
                    className="bg-[#f5f5f7] rounded-full px-3 py-1 text-[12px]"
                    data-testid={`role-select-${u.user_id}`}
                    disabled={u.user_id === me.user_id}
                  >
                    <option value="user">user</option>
                    <option value="agent">agent</option>
                    <option value="agency_owner">agency_owner</option>
                    <option value="admin">admin</option>
                  </select>
                </Td>
                <Td>
                  {u.user_id !== me.user_id && (
                    <button onClick={() => remove(u.user_id)} className="text-[#86868b] hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgenciesTab({ t, lang }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: "basique" });
  const [show, setShow] = useState(false);

  const load = () => api.get("/admin/agencies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/agencies", form);
      toast.success(lang === "fr" ? "Agence créée" : "Agency created");
      setForm({ name: "", email: "", phone: "", plan: "basique" });
      setShow(false);
      load();
    } catch { toast.error("Error"); }
  };

  const updatePlan = async (id, plan) => { await api.patch(`/admin/agencies/${id}`, { plan }); load(); };
  const remove = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/admin/agencies/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#1d1d1f]">{t.dashboard.agencies}</h1>
        <button onClick={() => setShow(!show)} className="btn-pill btn-primary text-[13px] flex items-center gap-2" data-testid="add-agency-btn">
          <Plus size={14} /> {t.dashboard.addAgency}
        </button>
      </div>
      {show && (
        <form onSubmit={submit} className="rounded-2xl bg-[#f5f5f7] p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="agency-form">
          <Inp label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="agency-name" />
          <Inp label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="agency-email" />
          <Inp label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="agency-phone" />
          <Sel label="Plan" value={form.plan} onChange={(v) => setForm({ ...form, plan: v })} options={[["basique","Basique"],["standard","Standard"],["premium","Premium"]]} testid="agency-plan" />
          <div className="md:col-span-2">
            <button type="submit" className="btn-pill btn-primary text-[13px]">Créer</button>
          </div>
        </form>
      )}
      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]" data-testid="agencies-table">
          <thead className="bg-[#f5f5f7] text-[#86868b]">
            <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Plan</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.agency_id} className="border-t border-black/5">
                <Td>{a.name}</Td>
                <Td className="text-[#86868b]">{a.email}</Td>
                <Td>{a.phone || "—"}</Td>
                <Td>
                  <select
                    value={a.plan}
                    onChange={(e) => updatePlan(a.agency_id, e.target.value)}
                    className="bg-[#f5f5f7] rounded-full px-3 py-1 text-[12px]"
                    data-testid={`agency-plan-${a.agency_id}`}
                  >
                    <option value="basique">basique</option>
                    <option value="standard">standard</option>
                    <option value="premium">premium</option>
                  </select>
                </Td>
                <Td>
                  <button onClick={() => remove(a.agency_id)} className="text-[#86868b] hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RolesTab({ lang }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/roles").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">
        {lang === "fr" ? "Rôles & permissions" : "Roles & permissions"}
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="roles-grid">
        {items.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-black/5 p-6">
            <div className="text-[11px] uppercase tracking-wider text-[#86868b]">{r.id}</div>
            <div className="text-[18px] font-medium mt-1 text-[#1d1d1f]">{r.name}</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {r.permissions.map((p) => (
                <span key={p} className="text-[11px] bg-[#f5f5f7] rounded-full px-3 py-1">{p}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesTab({ lang }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/messages").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">
        {lang === "fr" ? "Messages de contact" : "Contact messages"}
      </h1>
      {items.length === 0 ? (
        <div className="text-[#86868b]">{lang === "fr" ? "Aucun message" : "No messages"}</div>
      ) : (
        <div className="space-y-4" data-testid="messages-list">
          {items.map((m) => (
            <div key={m.message_id} className="rounded-2xl bg-white border border-black/5 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-[#1d1d1f]">{m.name}</div>
                <div className="text-[12px] text-[#86868b]">{m.email}</div>
              </div>
              <div className="text-[13px] text-[#86868b] mb-2">{m.subject}</div>
              <div className="text-[14px] text-[#1d1d1f]">{m.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Bookings tab ----
function BookingsTab({ lang }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(status === "confirmed"
      ? (lang === "fr" ? "Réservation confirmée" : "Booking confirmed")
      : (lang === "fr" ? "Réservation mise à jour" : "Booking updated"));
    load();
  };

  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">
        {lang === "fr" ? "Réservations" : "Bookings"}
      </h1>
      {items.length === 0 ? (
        <div className="text-[#86868b]">{lang === "fr" ? "Aucune réservation" : "No bookings"}</div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {items.map((b) => (
            <div key={b.booking_id} className="rounded-2xl bg-white border border-black/5 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`booking-${b.booking_id}`}>
              <div className="flex-1">
                <div className="text-[13px] text-[#86868b] mb-1">{b.property_title}</div>
                <div className="font-medium text-[#1d1d1f]">{b.guest_name} · {b.guest_email}</div>
                <div className="text-[13px] text-[#86868b] mt-1">
                  {b.check_in} → {b.check_out} · {b.nights} {lang === "fr" ? "nuits" : "nights"} · {b.guests_count} {lang === "fr" ? "pers." : "guests"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[18px] font-semibold">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(b.total_eur)}</div>
                <div className={`text-[11px] uppercase tracking-wider mt-1 ${
                  b.status === "confirmed" ? "text-green-600" : b.status === "cancelled" ? "text-red-500" : "text-[#86868b]"
                }`}>{b.status}</div>
              </div>
              <div className="flex gap-2">
                {b.status !== "confirmed" && (
                  <button onClick={() => update(b.booking_id, "confirmed")} className="btn-pill btn-primary text-[12px] py-2 px-4" data-testid={`confirm-booking-${b.booking_id}`}>
                    <CheckIcon size={13} /> {lang === "fr" ? "Confirmer" : "Confirm"}
                  </button>
                )}
                {b.status !== "cancelled" && (
                  <button onClick={() => update(b.booking_id, "cancelled")} className="btn-pill btn-secondary text-[12px] py-2 px-4" data-testid={`cancel-booking-${b.booking_id}`}>
                    <XIcon size={13} /> {lang === "fr" ? "Annuler" : "Cancel"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Applications tab ----
function ApplicationsTab({ lang }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/agency-applications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    await api.patch(`/admin/agency-applications/${id}`, { status });
    toast.success(status === "approved"
      ? (lang === "fr" ? "Candidature approuvée — agence créée" : "Approved — agency created")
      : (lang === "fr" ? "Candidature rejetée" : "Rejected"));
    load();
  };

  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight mb-8 text-[#1d1d1f]">
        {lang === "fr" ? "Candidatures agences" : "Agency applications"}
      </h1>
      {items.length === 0 ? (
        <div className="text-[#86868b]">{lang === "fr" ? "Aucune candidature" : "No applications"}</div>
      ) : (
        <div className="space-y-4" data-testid="applications-list">
          {items.map((a) => (
            <div key={a.application_id} className="rounded-2xl bg-white border border-black/5 p-6" data-testid={`application-${a.application_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-semibold text-[17px] text-[#1d1d1f]">{a.company_name}</div>
                  <div className="text-[13px] text-[#86868b]">{a.contact_name} · {a.email} {a.phone && `· ${a.phone}`}</div>
                </div>
                <div className={`text-[11px] uppercase tracking-wider px-3 py-1 rounded-full ${
                  a.status === "approved" ? "bg-green-50 text-green-600" :
                  a.status === "rejected" ? "bg-red-50 text-red-500" :
                  "bg-[#f5f5f7] text-[#86868b]"
                }`}>{a.status}</div>
              </div>
              <div className="text-[13px] text-[#86868b] mb-3">
                <span className="font-medium text-[#1d1d1f]">{a.city}, {a.country}</span>
                {a.website && <> · <a href={a.website} target="_blank" rel="noreferrer" className="underline">{a.website}</a></>}
                <> · Plan: <span className="capitalize font-medium text-[#1d1d1f]">{a.plan}</span></>
              </div>
              {a.message && <div className="text-[14px] text-[#1d1d1f] mb-4 italic">"{a.message}"</div>}
              {a.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => review(a.application_id, "approved")} className="btn-pill btn-primary text-[12px] py-2 px-4 flex items-center gap-1" data-testid={`approve-app-${a.application_id}`}>
                    <CheckIcon size={13} /> {lang === "fr" ? "Approuver" : "Approve"}
                  </button>
                  <button onClick={() => review(a.application_id, "rejected")} className="btn-pill btn-secondary text-[12px] py-2 px-4 flex items-center gap-1" data-testid={`reject-app-${a.application_id}`}>
                    <XIcon size={13} /> {lang === "fr" ? "Rejeter" : "Reject"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helpers
const Th = ({ children }) => <th className="text-left px-4 py-3 text-[11px] font-medium uppercase tracking-wider">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required className="w-full bg-white rounded-xl px-4 py-2 text-[14px] outline-none" data-testid={testid} />
    </div>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white rounded-xl px-4 py-2 text-[14px] outline-none" data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
